import React, { useEffect, useState, useRef } from "react";
import {
  NumericFormat,
  NumberFormatBase,
  useNumericFormat,
} from "react-number-format";
import { TextField, alpha } from "@mui/material";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import { TextFieldForViewOnly } from "./TextField";

const NEGATION_FORMAT_REGEX = /^\((.*)\)$/;

const disabledAmountTextFieldSx = {
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "text.primary",
  },
};

function extractNegationAndNumber(value) {
  let hasNegation = false;
  if (typeof value === "number") {
    hasNegation = value < 0;
    value = hasNegation ? value * -1 : value;
  } else if (value?.[0] === "-") {
    hasNegation = true;
    value = value.substring(1);
  } else if (value?.match(NEGATION_FORMAT_REGEX)) {
    hasNegation = true;
    value = value.replace(NEGATION_FORMAT_REGEX, "$1");
  }
  return { hasNegation, value };
}

function CustomNegationNumberFormat({
  prefix = "",
  suffix = "",
  value,
  defaultValue,
  onValueChange,
  ...restProps
}) {
  const [hasNegation, toggleNegation] = useState(
    extractNegationAndNumber(value ?? defaultValue).hasNegation
  );
  const [internalValue, setInternalValue] = useState(
    extractNegationAndNumber(value ?? defaultValue).value
  );

  useEffect(() => {
    const { value: internalValue } = extractNegationAndNumber(value);
    setInternalValue(internalValue);
  }, [value]);

  const _onValueChange = (values, sourceInfo) => {
    if (!onValueChange) return;

    const { formattedValue, value, floatValue } = values;
    const numericValue = hasNegation ? `-${value}` : value;
    const numericFloatValue =
      hasNegation && !isNaN(floatValue) ? -floatValue : floatValue;

    onValueChange(
      {
        formattedValue,
        value: numericValue,
        floatValue: numericFloatValue,
      },
      sourceInfo
    );
  };

  const props = {
    prefix: hasNegation ? "(" + prefix : prefix,
    suffix: hasNegation ? suffix + ")" : suffix,
    allowNegative: false,
    value: internalValue,
    onValueChange: _onValueChange,
    ...restProps,
  };

  const { format, onKeyDown, ...numberFormatBaseProps } =
    useNumericFormat(props);

  const _format = (numStr) => {
    const formattedValue = format(numStr, props);
    return formattedValue === "" && hasNegation
      ? props.prefix + props.suffix
      : formattedValue;
  };

  const _onKeyDown = (e) => {
    const el = e.target;
    const { key } = e;
    const { selectionStart, selectionEnd, value = "" } = el;

    if (selectionStart !== selectionEnd) {
      onKeyDown(e);
      return;
    }

    if (key === "-") {
      toggleNegation((prev) => {
        const newNegationState = !prev;

        // Trigger onValueChange manually to force update
        setTimeout(() => {
          if (onValueChange) {
            const numericValue = newNegationState
              ? `-${internalValue || "0"}`
              : internalValue;
            const numericFloatValue = newNegationState
              ? -Number(internalValue || "0")
              : Number(internalValue || "0");

            onValueChange(
              {
                formattedValue: newNegationState
                  ? `(${internalValue || "0"})`
                  : internalValue,
                value: numericValue,
                floatValue: numericFloatValue,
              },
              { event: e, source: "keypress" }
            );
          }
        }, 0);

        return newNegationState;
      });
    }

    onKeyDown(e);
  };

  return (
    <NumberFormatBase
      {...numberFormatBaseProps}
      onKeyDown={_onKeyDown}
      format={_format}
    />
  );
}

export const NumericTextField = ({ onChange, value, label }) => {
  return (
    <NumericFormat
      label={label}
      value={value}
      onChange={onChange}
      customInput={TextField}
      thousandSeparator
      valueIsNumericString
    />
  );
};

export const TextFieldForDecimalAmount = React.memo(
  ({
    value,
    onChange,
    reference = null,
    label = "",
    fullWidth = false,
    disabled = false,
    forceNegative = false,
    canEdit = true,
    sx = {},
    decimalScale,
    fixedDecimalScale,
  }) => {
    const handleChange = (e) => {
      let rawValue = e.target.value;

      if (rawValue === "") {
        onChange(0, reference);
        return;
      }

      let cleanedValue = rawValue.replace(/[()]/g, "");

      if ((rawValue.includes("(") && rawValue.includes(")")) || forceNegative) {
        cleanedValue = `-${cleanedValue}`;
      }

      onChange(cleanedValue, reference);
    };

    if (!canEdit) {
      return (
        <TextFieldForViewOnly
          value={formatAmount(value)}
          sx={{ textAlign: "right" }}
        />
      );
    }

    return (
      <CustomNegationNumberFormat
        label={label}
        value={forceNegative ? `-${value}` : value}
        onChange={(e) => handleChange(e)}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        decimalScale={decimalScale}
        fixedDecimalScale={fixedDecimalScale}
        size='small'
        inputProps={{
          style: { textAlign: "right" },
        }}
        variant='outlined'
        fullWidth={fullWidth}
        disabled={disabled}
        sx={[
          disabled ? disabledAmountTextFieldSx : null,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        autoComplete='off'
      />
    );
  }
);

export const TextFieldForAmount = React.memo(
  ({
    value,
    onChange,
    reference = null,
    label = "",
    fullWidth = false,
    disabled = false,
    forceNegative = false,
    canEdit = true,
    sx = {},
  }) => {
    const handleChange = (e) => {
      let rawValue = e.target.value;

      if (rawValue === "") {
        onChange(0, reference);
        return;
      }

      let cleanedValue = rawValue.replace(/[()]/g, "");

      if ((rawValue.includes("(") && rawValue.includes(")")) || forceNegative) {
        cleanedValue = `-${cleanedValue}`;
      }

      onChange(cleanedValue, reference);
    };

    if (!canEdit) {
      return (
        <TextFieldForViewOnly
          value={formatAmount(value)}
          sx={{ textAlign: "right" }}
        />
      );
    }

    return (
      <CustomNegationNumberFormat
        label={label}
        value={forceNegative ? `-${value}` : value}
        onChange={(e) => handleChange(e)}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        size='small'
        inputProps={{
          style: { textAlign: "right" },
        }}
        variant='outlined'
        fullWidth={fullWidth}
        disabled={disabled}
        sx={[
          disabled ? disabledAmountTextFieldSx : null,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        autoComplete='off'
      />
    );
  }
);

export function arrayToExpression(array) {
  if (!Array.isArray(array)) return array;

  return array
    .map((num, index) => {
      if (index === 0) return num.toString();
      return num >= 0 ? `+${formatAmount(num)}` : num.toString();
    })
    .join("");
}

function expressionToArray(expr) {
  if (!expr) return [];
  const tokens = expr.split(/([+-])/);
  const result = [];
  let sign = 1;

  for (let token of tokens) {
    if (token === "+") {
      sign = 1;
    } else if (token === "-") {
      sign = -1;
    } else if (token.trim() !== "") {
      const num = parseFloat(token.replace(/,/g, ""));
      if (!isNaN(num)) {
        result.push(sign * num);
      }
    }
  }

  return result;
}

export const CalculatorTextField = ({
  label = "",
  value = [],
  onChange,
  reference,
  fullWidth = false,
  disabled = false,
  needRound = true,
  allowDecimal = false,
  sx = {},
  canEdit = true,
}) => {
  const [focused, setFocused] = useState(false);
  const [expression, setExpression] = useState(arrayToExpression(value));
  const inputRef = useRef();

  useEffect(() => {
    if (!focused) {
      setExpression(arrayToExpression(value));
    }
  }, [value, focused]);

  const handleBlur = () => {
    const parsedArray = expressionToArray(expression);
    setExpression(arrayToExpression(parsedArray)); // normalize
    setFocused(false);
    if (onChange) onChange(parsedArray, reference);
  };

  const handleChange = (e) => {
    let val = e.target.value;

    if (!allowDecimal) {
      val = val.split(".")[0] || "0";
    }

    setExpression(val);
  };

  const sum = value.map(Number).reduce((acc, val) => acc + val, 0);

  if (!canEdit) {
    return <TextFieldForViewOnly value={formatAmount(sum, needRound)} />;
  }

  return (
    <TextField
      inputRef={inputRef}
      label={label}
      fullWidth={fullWidth}
      variant='outlined'
      size='small'
      value={focused ? expression : formatAmount(sum, needRound)}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      onChange={handleChange}
      inputProps={{ style: { textAlign: "right" }, autoComplete: "off" }}
      disabled={disabled}
      sx={sx}
    />
  );
};
